import { sortMembersByType, teamsClient } from '@atlassian/teams-client';
import { isResultWithPageInfo, type TeamMembership } from '@atlassian/teams-client/types';

import type { StoreApi } from '../types';

const combineIn = (existing: TeamMembership[], newData: TeamMembership[]) => {
	const addedMemberIds = new Set(newData.map((member) => member.membershipId.memberId));

	return existing
		.filter((member) => !addedMemberIds.has(member.membershipId.memberId))
		.concat(newData);
};

export const getTeamMembers =
	(teamId: string, keepExistingData?: boolean, first: number = 100) =>
	async ({ getState, setState }: StoreApi) => {
		if (!keepExistingData) {
			setState({
				teamMembers: {
					teamId,
					isLoading: true,
					isLoadingNextPage: false,
					hasLoaded: false,
					hasError: false,
					errorType: null,
					members: [],
					requestingToJoinUsers: [],
					membersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
					requestingToJoinUsersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
				},
			});
		}
		const { teamMembers } = getState();

		if (teamMembers.teamId !== teamId) {
			return;
		}

		setState({
			teamMembers: {
				...teamMembers,
				isLoadingNextPage: true,
			},
		});

		try {
			const membershipsResponse = await teamsClient.queryTeamMemberships(teamId, ['FULL_MEMBER'], {
				first,
				after: teamMembers.membersPageInfo.cursor ?? undefined,
			});

			const memberships = isResultWithPageInfo<TeamMembership>(membershipsResponse)
				? membershipsResponse.edges
				: membershipsResponse;

			const pageInfo = isResultWithPageInfo<TeamMembership>(membershipsResponse)
				? membershipsResponse.pageInfo
				: { hasNextPage: false, endCursor: null };

			const myMembership = getState().myTeamMembership.membership;
			const myAccountId = myMembership?.membershipId.memberId;

			const membershipsWithExisting = keepExistingData
				? combineIn(teamMembers.members, memberships)
				: memberships;

			const sortedMembers = sortMembersByType(membershipsWithExisting, myAccountId);

			setState({
				teamMembers: {
					teamId,
					isLoading: false,
					isLoadingNextPage: false,
					hasLoaded: true,
					hasError: false,
					errorType: null,
					members: sortedMembers,
					requestingToJoinUsers: [],
					requestingToJoinUsersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
					membersPageInfo: {
						hasNextPage: pageInfo.hasNextPage,
						cursor: pageInfo.endCursor,
					},
				},
			});
		} catch (e) {
			if (getState().teamMembers.teamId !== teamId) {
				return;
			}

			setState({
				teamMembers: {
					teamId,
					isLoading: false,
					isLoadingNextPage: false,
					hasLoaded: true,
					hasError: true,
					errorType: e as Error,
					members: [],
					requestingToJoinUsers: [],
					membersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
					requestingToJoinUsersPageInfo: {
						hasNextPage: false,
						cursor: null,
					},
				},
			});
		}
	};
